import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet";
import { toast, useToast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Consultation } from "@/types/consultation";
import { ConsultationStatus } from "@/types/enums";
import {
  useCreateConsultationMutation,
  useUpdateConsultationMutation,
} from "@/services/consultationApi";
import { FormProps } from "@/types";
import { ErrorType } from "@/types";
import Background from "./steps/background";
import BodyMeasurements from "./steps/body-measurements";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

// Placeholder step components - to be replaced with actual step components later
const Step1Component = (props: FormStepProps) => <Background {...props} />;
const Step2Component = (props: FormStepProps) => <BodyMeasurements {...props} />;
const Step3Component = (_props: FormStepProps) => <div>Cardio Test - Placeholder</div>;
const Step4Component = (_props: FormStepProps) => <div>Flexibility Test - Placeholder</div>;
const Step5Component = (_props: FormStepProps) => <div>Endurance Test - Placeholder</div>;
const Step6Component = (_props: FormStepProps) => <div>Posture Analysis - Placeholder</div>;
const Step7Component = (_props: FormStepProps) => <div>Goals - Placeholder</div>;
const Step8Component = (_props: FormStepProps) => <div>Meal Plan - Placeholder</div>;
const Step9Component = (_props: FormStepProps) => <div>Workout Plan - Placeholder</div>;

const stepContentComponents = [
  Step1Component,
  Step2Component,
  Step3Component,
  Step4Component,
  Step5Component,
  Step6Component,
  Step7Component,
  Step8Component,
  Step9Component,
];

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? <Component {...formProps} /> : null;
};

const ConsultationForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
}: FormProps<Consultation>) => {
  const { toast } = useToast();
  const [activeStep, setActiveStep] = useState(1);
  const [createConsultation] = useCreateConsultationMutation();
  const [updateConsultation] = useUpdateConsultationMutation();

  // Define initial values for the form
  const initialValues: Consultation = {
    member_id: 0,
    member_name: "",
    coach_id: 0,
    coach_name: "",
    date: new Date().toISOString(),
    status: ConsultationStatus.Pending,
    notes: "",
    body_measurements: {},
  };

  // Define steps configuration
  const steps = useMemo(
    () => [
      { label: "Background", key: 1 },
      { label: "Body Measurements", key: 2 },
      { label: "Cardio Test", key: 3 },
      { label: "Flexibility Test", key: 4 },
      { label: "Endurance Test", key: 5 },
      { label: "Posture Analysis", key: 6 },
      { label: "Goals", key: 7 },
      { label: "Meal Plan", key: 8 },
      { label: "Workout Plan", key: 9 },
    ],
    []
  );

  const form = useForm<Consultation>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    clearErrors,
    reset,
    trigger,
    watch,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();
  console.log({ watcher, errors })
  // Reset form when dialog opens/closes or action changes
  useEffect(() => {
    if (!isOpen) return;
    if (action === "edit" && data) {
      reset(data as Consultation);
    } else if (action === "add") {
      setActiveStep(1);
      clearErrors();
      reset(initialValues);
    }
  }, [action, isOpen, data, reset, clearErrors]);

  // Ensure step index never exceeds steps length
  useEffect(() => {
    if (activeStep > steps.length) {
      setActiveStep(1);
    }
  }, [steps.length]);

  const onSubmit = async (formData: Consultation) => {
    try {
      // Add any form-wide validation here before submission

      const payload = {
        ...formData,
      };

      if (action === "add") {
        await createConsultation(payload as Consultation).unwrap();
        toast({
          description: "Consultation created successfully",
          variant: "success",
        });
      } else {
        await updateConsultation(payload as Consultation).unwrap();
        toast({
          description: "Consultation updated successfully",
          variant: "success",
        });
      }
      refetch();
      handleClose();
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ||
            (typedError.data as { message?: string }).message
            }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setActiveStep(1);
    setAction("add");
    setData(undefined);
    reset(initialValues);
    setOpen(false);
  };

  const handleNext = async () => {
    const isStepValid = await trigger(undefined, { shouldFocus: true });

    if (isStepValid && activeStep < steps.length) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handlePrev = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1000px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={handleClose}>
                    <i className="fa fa-xmark text-xl"></i>
                  </button>
                  <div>
                    <h1 className="font-medium">
                      {action == "add" ? "Create" : "Edit"} Consultation
                    </h1>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {activeStep !== 1 && (
                    <Button
                      className="w-[80px]"
                      variant="outline"
                      onClick={handlePrev}
                      disabled={activeStep === 1}
                      type="button"
                    >
                      Previous
                    </Button>
                  )}
                  {activeStep !== steps.length && (
                    <Button
                      className="w-[80px]"
                      onClick={handleNext}
                      disabled={activeStep === steps.length}
                      type="button"
                    >
                      Next
                    </Button>
                  )}
                  {activeStep === steps.length && (
                    <LoadingButton
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      type="submit"
                      className="w-[80px]"
                    >
                      {!isSubmitting && (
                        <i className="fa fa-floppy-disk pr-2"></i>
                      )}
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  )}
                </div>
              </div>
            </SheetHeader>

            <SheetDescription className="h-full p-6">
              <div className="flex gap-x-6">
                {/* Vertical Stepper - Sticky on the left */}
                <div className="flex-shrink-0 w-48">
                  <CompactStepIndicator
                    activeStep={activeStep}
                    steps={steps}
                    orientation="vertical"
                  />
                </div>

                {/* Form Content - Scrollable on the right */}
                <div className="flex-1">
                  {getStepContent(activeStep, { action, open: isOpen })}
                </div>
              </div>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default ConsultationForm;
